import { yupResolver } from "@hookform/resolvers/yup"
import Stack from "@mui/material/Stack"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import * as yup from "yup"

import DialogProvider from "@components/dialog_provider"
import ConfirmForceUpdate from "@components/dialogs/confirm_force_update"
import TextInput from "@components/text_input"
import { updateMobileAppVersionConfig } from "@domain/mobile_app_version"
import { type MobileAppVersionConfig } from "@models/mobile_app_version"
import useControlledForm from "@hooks/use_controlled_form"
import useTranslate from "@hooks/use_translate"
import { compareVersions } from "@utils/functions/compare_versions"
import { showSuccessToast } from "@utils/toast"

import ReleaseNotesTabs from "./release_notes_tabs"
import type { MobileAppVersionFormData } from "./model"

const SEMVER = /^\d+\.\d+\.\d+$/

const toFormData = (
  config: MobileAppVersionConfig
): MobileAppVersionFormData => ({
  platform: config.platform,
  latestVersion: config.latestVersion ?? "",
  minSupportedVersion: config.minSupportedVersion ?? "",
  updateType: config.updateType ?? "NONE",
  storeUrl: config.storeUrl ?? "",
  releaseNotes: {
    tr: {
      title: config.releaseNotes?.tr?.title ?? "",
      body: config.releaseNotes?.tr?.body ?? ""
    },
    en: {
      title: config.releaseNotes?.en?.title ?? "",
      body: config.releaseNotes?.en?.body ?? ""
    },
    ar: {
      title: config.releaseNotes?.ar?.title ?? "",
      body: config.releaseNotes?.ar?.body ?? ""
    }
  }
})

export default function UpdateMobileAppVersion({
  isOpen,
  setClose,
  config
}: {
  isOpen: boolean
  setClose: () => void
  config: MobileAppVersionConfig
}) {
  const { translate } = useTranslate()
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const schema = yup.object({
    platform: yup.string().oneOf(["IOS", "ANDROID"]).required(),
    latestVersion: yup
      .string()
      .matches(SEMVER, translate("mobile_app_version_invalid_semver"))
      .required(translate("mobile_app_version_invalid_semver")),
    minSupportedVersion: yup
      .string()
      .matches(SEMVER, translate("mobile_app_version_invalid_semver"))
      .required(translate("mobile_app_version_invalid_semver"))
      // Mirrors the server guard in mobileAppVersion.utils.assertVersionConfigValid.
      .test(
        "min-lte-latest",
        translate("mobile_app_version_min_above_latest"),
        (value, ctx) => {
          // yup types `ctx.parent` as `any`; narrow it once instead of
          // threading unsafe values into compareVersions.
          const latest = String(
            (ctx.parent as { latestVersion?: string })?.latestVersion ?? ""
          )
          if (!value || !SEMVER.test(latest)) return true
          return compareVersions(value, latest) <= 0
        }
      ),
    updateType: yup.string().oneOf(["NONE", "OPTIONAL", "FORCED"]).required(),
    storeUrl: yup
      .string()
      .url(translate("mobile_app_version_invalid_url"))
      .required(translate("mobile_app_version_invalid_url")),
    // Release notes are optional in every locale: not every version bump has
    // something worth announcing, and requiring one only produces filler text.
    // The mobile modal renders the notes section only when a note exists.
    releaseNotes: yup.object({
      tr: yup.object({ title: yup.string(), body: yup.string() }),
      en: yup.object({ title: yup.string(), body: yup.string() }),
      ar: yup.object({ title: yup.string(), body: yup.string() })
    })
  })

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors, isValid }
  } = useControlledForm<MobileAppVersionFormData>({
    resolver: yupResolver(schema) as never,
    mode: "all",
    defaultValues: toFormData(config)
  })

  useEffect(() => {
    if (isOpen) reset(toFormData(config))
  }, [isOpen, config, reset])

  const persist = async (data: MobileAppVersionFormData) => {
    setLoading(true)
    const response = await updateMobileAppVersionConfig({
      platform: data.platform,
      latestVersion: data.latestVersion.trim(),
      minSupportedVersion: data.minSupportedVersion.trim(),
      updateType: data.updateType,
      storeUrl: data.storeUrl.trim(),
      releaseNotes: data.releaseNotes
    })
    setLoading(false)

    // graphql.ts already toasts the server error; keep the dialog open on failure.
    if (!response.isSuccess) return

    showSuccessToast(translate("mobile_app_version_saved"))
    await queryClient.invalidateQueries({
      queryKey: ["mobile-app-version-configs"]
    })
    setConfirmOpen(false)
    setClose()
  }

  /**
   * Only confirm when it actually matters — raising the floor or switching to
   * FORCED. Confirming every save trains people to click past the dialog.
   */
  const onSubmit = (data: MobileAppVersionFormData) => {
    const raisesFloor =
      !!config.minSupportedVersion &&
      compareVersions(data.minSupportedVersion, config.minSupportedVersion) > 0
    const isDangerous = raisesFloor || data.updateType === "FORCED"

    if (isDangerous) {
      setConfirmOpen(true)
      return
    }
    persist(data)
  }

  return (
    <>
      <DialogProvider
        title={translate("mobile_app_version_edit")}
        isOpen={isOpen}
        setClose={setClose}
        size="medium"
        leftButton={{
          text: translate("cancel"),
          color: "secondary",
          onClick: setClose,
          disabled: loading
        }}
        rightButton={{
          text: translate("save"),
          color: "primary",
          onClick: handleSubmit(onSubmit),
          loading,
          disabled: !isValid || loading
        }}
      >
        <Stack gap={2}>
          <TextInput<MobileAppVersionFormData>
            name="latestVersion"
            control={control}
            disabled={loading}
            label={translate("mobile_app_version_latest")}
            placeholder="2.11.0"
            required
          />
          <TextInput<MobileAppVersionFormData>
            name="minSupportedVersion"
            control={control}
            disabled={loading}
            label={translate("mobile_app_version_min_supported")}
            placeholder="2.5.0"
            required
          />
          <TextInput
            type="select"
            name="updateType"
            control={control}
            disabled={loading}
            label={translate("mobile_app_version_update_type")}
            placeholder={translate("mobile_app_version_update_type")}
            selectOptions={[
              {
                label: translate("mobile_app_version_type_none"),
                value: "NONE"
              },
              {
                label: translate("mobile_app_version_type_optional"),
                value: "OPTIONAL"
              },
              {
                label: translate("mobile_app_version_type_forced"),
                value: "FORCED"
              }
            ]}
            required
          />
          <TextInput<MobileAppVersionFormData>
            name="storeUrl"
            control={control}
            disabled={loading}
            label={translate("mobile_app_version_store_url")}
            placeholder="https://..."
            required
          />

          <ReleaseNotesTabs
            control={control}
            errors={errors}
            translate={translate}
            disabled={loading}
          />
        </Stack>
      </DialogProvider>

      <ConfirmForceUpdate
        isOpen={confirmOpen}
        setClose={() => setConfirmOpen(false)}
        loading={loading}
        platform={config.platform}
        savedMinVersion={config.minSupportedVersion ?? ""}
        nextMinVersion={getValues("minSupportedVersion")}
        savedLatestVersion={config.latestVersion ?? ""}
        nextLatestVersion={getValues("latestVersion")}
        onConfirm={() => persist(getValues())}
      />
    </>
  )
}
